import { LlmProvider, ProviderRequest, StreamEvent, Message } from '../types'

// Direct browser-to-OpenAI access using the user's own key (BYOK).
// api.openai.com sends permissive CORS headers, so a browser fetch with the
// key in the Authorization header works without a proxy (no server cost).
const ENDPOINT = 'https://api.openai.com/v1/chat/completions'

// Translate our normalized (Anthropic-style) messages into OpenAI chat messages.
// - assistant tool_use blocks -> assistant.tool_calls
// - user tool_result blocks    -> separate { role: 'tool', tool_call_id } messages
function toOpenAiMessages(system: string, messages: Message[]): any[] {
	const out: any[] = [ { role: 'system', content: system } ]
	for (const m of messages) {
		if (m.role === 'user') {
			const toolResults = m.content.filter(b => b.type === 'tool_result') as any[]
			if (toolResults.length) {
				for (const b of toolResults) out.push({ role: 'tool', tool_call_id: b.tool_use_id, content: b.content })
			}
			else {
				const text = m.content.filter(b => b.type === 'text').map((b: any) => b.text).join('\n')
				const docs = m.content.filter(b => b.type === 'document') as any[]
				if (docs.length) {
					const parts: any[] = []
					if (text) parts.push({ type: 'text', text })
					for (const d of docs) {
						parts.push({ type: 'file', file: { filename: d.name, file_data: `data:${ d.mediaType };base64,${ d.data }` } })
					}
					out.push({ role: 'user', content: parts })
				}
				else if (text) {
					out.push({ role: 'user', content: text })
				}
			}
		}
		else {
			const text = m.content.filter(b => b.type === 'text').map((b: any) => b.text).join('')
			const toolUses = m.content.filter(b => b.type === 'tool_use') as any[]
			const msg: any = { role: 'assistant', content: text ? text : null }
			if (toolUses.length) {
				msg.tool_calls = toolUses.map(t => ({
					id: t.id,
					type: 'function',
					function: { name: t.name, arguments: JSON.stringify(t.input ?? {}) },
				}))
			}
			out.push(msg)
		}
	}
	return out
}

function mapFinish(reason: string): string {
	if (reason === 'tool_calls') return 'tool_use'
	if (reason === 'length') return 'max_tokens'
	return 'end_turn'
}

export const openaiProvider: LlmProvider = {
	id: 'openai',
	label: 'OpenAI (GPT)',
	models: [ 'gpt-4o', 'gpt-4o-mini' ],
	defaultModel: 'gpt-4o',

	async *stream(req: ProviderRequest): AsyncGenerator<StreamEvent> {
		const body: any = {
			model: req.model,
			max_tokens: req.maxTokens ?? 4096,
			messages: toOpenAiMessages(req.system, req.messages),
			stream: true,
		}
		if (req.tools.length) {
			body.tools = req.tools.map(t => ({
				type: 'function',
				function: { name: t.name, description: t.description, parameters: t.input_schema },
			}))
		}

		let res: Response
		try {
			res = await fetch(ENDPOINT, {
				method: 'POST',
				headers: {
					'content-type': 'application/json',
					authorization: 'Bearer ' + req.apiKey,
				},
				body: JSON.stringify(body),
				signal: req.signal,
			})
		}
		catch (e: any) {
			yield { type: 'error', error: 'Network error: ' + (e?.message ?? e) }
			return
		}

		if (!res.ok || !res.body) {
			const text = await res.text().catch(() => '')
			yield { type: 'error', error: `HTTP ${ res.status }: ${ text }` }
			return
		}

		const reader = res.body.getReader()
		const decoder = new TextDecoder()
		let buf = ''
		// OpenAI streams a text block plus 0..n tool calls, each keyed by its own
		// `index`. Map text -> our block 0 and tool call k -> our block k+1 so the
		// agent sees unique, non-colliding block indices.
		const startedTools = new Set<number>()
		let finished = false
		let stopReason = 'end_turn'

		const finish = function* (): Generator<StreamEvent> {
			for (const idx of startedTools) yield { type: 'block_stop', index: idx + 1 }
			yield { type: 'done', stopReason }
			finished = true
		}

		while (true) {
			const { done, value } = await reader.read()
			if (done) break
			buf += decoder.decode(value, { stream: true })

			let sep
			while ((sep = buf.indexOf('\n\n')) !== -1) {
				const chunk = buf.slice(0, sep)
				buf = buf.slice(sep + 2)

				let dataStr = ''
				for (const line of chunk.split('\n')) {
					if (line.startsWith('data:')) dataStr += line.slice(5).trim()
				}
				if (!dataStr) continue
				if (dataStr === '[DONE]') { if (!finished) yield* finish(); continue }

				let obj: any
				try { obj = JSON.parse(dataStr) }
				catch { continue }

				const choice = obj.choices?.[0]
				if (!choice) continue
				const delta = choice.delta ?? {}

				if (delta.content) {
					yield { type: 'text_delta', index: 0, text: delta.content }
				}
				if (Array.isArray(delta.tool_calls)) {
					for (const tc of delta.tool_calls) {
						const idx = tc.index ?? 0
						if (!startedTools.has(idx)) {
							startedTools.add(idx)
							yield { type: 'tool_use_start', index: idx + 1, id: tc.id, name: tc.function?.name ?? '' }
						}
						if (tc.function?.arguments) {
							yield { type: 'tool_use_input_delta', index: idx + 1, partialJson: tc.function.arguments }
						}
					}
				}
				if (choice.finish_reason && !finished) {
					stopReason = mapFinish(choice.finish_reason)
					yield* finish()
				}
			}
		}

		if (!finished) yield* finish()
	},
}
